import { useEffect, useMemo, useRef, useState } from 'react';
import { Zap, ChevronUp, ChevronDown } from 'lucide-react';
import { Button, IconButton, Input, Badge, SegmentedControl } from '@vnetwork/ui/primitives';
import { useToast, Alert } from '@vnetwork/ui/feedback';
import type { CdnProvider } from '@/api/manticore/cdn-providers';
import { cdnProviderLabel } from './cdn-provider-display';
import { brand } from '@/config/brand';
import styles from './CdnLoadBalancer.module.css';

type Strategy = 'failover' | 'weighted' | 'latency';

const STRATEGIES: { value: Strategy; label: string; desc: string }[] = [
  { value: 'failover', label: 'Failover', desc: 'Primary provider serves all traffic; standbys take over automatically if it turns unhealthy.' },
  { value: 'weighted', label: 'Weighted', desc: 'Split traffic across active providers by the weights you set below.' },
  { value: 'latency', label: 'Latency-based', desc: 'Each request is routed to the healthiest, lowest-latency provider automatically.' },
];

interface LbState {
  strategy: Strategy;
  weights: Record<string, number>;
  order: string[];
}

/** Seed a default config from the active provider list (canonical first, equal split). */
function seed(active: CdnProvider[]): LbState {
  const order = [...active]
    .sort((a, b) => Number(b.is_canonical) - Number(a.is_canonical))
    .map((p) => p.provider_name);
  const even = active.length ? Math.round(100 / active.length) : 0;
  const weights: Record<string, number> = {};
  for (const p of active) weights[p.provider_name] = even;
  return { strategy: 'latency', weights, order };
}

/**
 * Multi-CDN Load Balancer — configures how traffic is distributed across the
 * orchestrator's active provider backends. Rendered inside the CDN Providers
 * Section. FE state only for now (Save is local); the BE config endpoint is wired
 * in a follow-up. Only active providers participate; the provider set itself is
 * managed by the on/off toggles above — this never changes which providers exist.
 */
export function CdnLoadBalancer({ providers }: { providers: CdnProvider[] }) {
  const { show } = useToast();
  const active = useMemo(() => providers.filter((p) => p.status === 'active'), [providers]);
  const sig = active.map((p) => p.provider_name).join(',');

  const [state, setState] = useState<LbState>(() => seed(active));
  const baseline = useRef<string>(JSON.stringify(state));

  // Re-seed whenever the active provider set changes (e.g. a backend toggled off).
  useEffect(() => {
    const next = seed(active);
    setState(next);
    baseline.current = JSON.stringify(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  const dirty = JSON.stringify(state) !== baseline.current;

  const totalWeight = state.order.reduce((s, n) => s + (state.weights[n] || 0), 0);
  const share = (name: string) =>
    totalWeight > 0 ? Math.round(((state.weights[name] || 0) / totalWeight) * 100) : 0;

  const setStrategy = (v: string) => setState((s) => ({ ...s, strategy: v as Strategy }));
  const setWeight = (name: string, w: number) =>
    setState((s) => ({ ...s, weights: { ...s.weights, [name]: Math.max(0, Math.min(100, w)) } }));
  const move = (i: number, dir: -1 | 1) =>
    setState((s) => {
      const order = [...s.order];
      const j = i + dir;
      if (j < 0 || j >= order.length) return s;
      [order[i], order[j]] = [order[j], order[i]];
      return { ...s, order };
    });

  const save = () => {
    baseline.current = JSON.stringify(state);
    setState((s) => ({ ...s })); // trigger re-render so `dirty` recomputes to false
    show({ tone: 'success', title: 'Load balancer settings applied' });
  };
  const reset = () => setState(JSON.parse(baseline.current));

  const selected = STRATEGIES.find((s) => s.value === state.strategy)!;

  return (
    <div className={styles.lb}>
      <div className={styles.head}>
        <span className={styles.headTitle}>Multi-CDN Load Balancer</span>
        <span className={styles.headDesc}>Choose how requests are distributed across active provider backends.</span>
      </div>

      <Alert tone="info">
        Load balancing is currently controlled by the {brand.shortName} Multi-CDN Orchestrator Control Plane and
        will be released for customer self-service soon. For any inquiries, contact{' '}
        <a href={`mailto:${brand.emails.support}`} style={{ color: 'inherit' }}>{brand.emails.support}</a>.
      </Alert>

      {active.length < 2 ? (
        <span className={styles.hint}>Enable a second provider backend above to configure load balancing.</span>
      ) : (
        <>
          {/* Strategy is locked to the control plane's active mode — tabs are
              read-only until customer self-service is released. */}
          <SegmentedControl
            aria-label="Load balancing strategy"
            items={STRATEGIES.map((s) => ({ value: s.value, label: s.label, disabled: true }))}
            value={state.strategy}
            onValueChange={setStrategy}
          />
          <p className={styles.strategyDesc}>{selected.desc}</p>

          {state.strategy === 'weighted' && (
            <div className={styles.panel}>
              {state.order.map((name) => (
                <div key={name} className={styles.row}>
                  <div className={styles.rowMain}>
                    <span className={styles.rowName}>{cdnProviderLabel(name)}</span>
                    <span className={styles.rowScope}>{name}</span>
                  </div>
                  <span className={styles.weightInput}>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={String(state.weights[name] ?? 0)}
                      onChange={(e) => setWeight(name, Number(e.target.value) || 0)}
                      aria-label={`Weight for ${name}`}
                    />
                  </span>
                  <div className={styles.shareTrack}>
                    <div className={styles.shareFill} style={{ width: `${share(name)}%` }} />
                  </div>
                  <span className={styles.shareLabel}>{share(name)}%</span>
                </div>
              ))}
            </div>
          )}

          {state.strategy === 'failover' && (
            <div className={styles.panel}>
              {state.order.map((name, i) => (
                <div key={name} className={styles.row}>
                  <span className={styles.priority}>{i + 1}</span>
                  <div className={styles.rowMain}>
                    <span className={styles.rowName}>{cdnProviderLabel(name)}</span>
                    <span className={styles.rowScope}>{name}</span>
                  </div>
                  {i === 0 && (
                    <span className={styles.primaryTag}>
                      <Badge tone="success" appearance="subtle" size="sm">Primary</Badge>
                    </span>
                  )}
                  <div className={styles.reorder}>
                    <IconButton aria-label={`Move ${name} up`} size="sm" disabled={i === 0} onClick={() => move(i, -1)}>
                      <ChevronUp size={15} />
                    </IconButton>
                    <IconButton aria-label={`Move ${name} down`} size="sm" disabled={i === state.order.length - 1} onClick={() => move(i, 1)}>
                      <ChevronDown size={15} />
                    </IconButton>
                  </div>
                </div>
              ))}
            </div>
          )}

          {state.strategy === 'latency' && (
            <div className={styles.panel}>
              <div className={styles.callout}>
                <Zap size={16} className={styles.calloutIcon} />
                <span>
                  Traffic is steered to the lowest-latency healthy provider in real time using the same health
                  probes shown above. No manual weights or priority needed.
                </span>
              </div>
            </div>
          )}

          <div className={styles.footer}>
            <Button variant="secondary" size="sm" disabled={!dirty} onClick={reset}>Reset</Button>
            <Button size="sm" disabled={!dirty} onClick={save}>Save</Button>
          </div>
        </>
      )}
    </div>
  );
}
